import { Layer, LayerProps } from 'shared/map-exports';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { usePoisState } from 'state/usePoisState';
import {
  BELOW_STREETS_LAYER_ID,
  OVERLAY_ICONS_BEFORE_ID,
} from 'features/mapping/styles';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { addVisible } from 'shared/addVisible';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import { PowerOutageIcon } from 'features/mapping/icons';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { getWatchdutyTileUrl } from '../layers.utils';
import {
  SOURCE_LAYER,
  SOURCE_ID,
  REFRESH_INTERVAL_SECONDS,
  AREA_FILL_LAYER_ID,
  AREA_STROKE_LAYER_ID,
  AREA_SELECTED_FILL_LAYER_ID,
  AREA_SELECTED_STROKE_LAYER_ID,
  ITEM_SHADOW_LAYER_ID,
  ITEM_BACKGROUND_LAYER_ID,
  ITEM_SELECTED_BACKGROUND_LAYER_ID,
  ITEM_ICON_LAYER_ID,
  MIN_ZOOM,
} from './PowerOutageLayer.constants';
import { getSelectedPowerOutageId } from './PowerOutageLayer.utils';

// Area layer styles
const areaFillStyle: LayerProps = {
  type: 'fill',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM,
  filter: ['==', ['geometry-type'], 'Polygon'],
  paint: {
    'fill-pattern': 'PowerOutagePattern',
  },
};

const areaStrokeStyle: LayerProps = {
  type: 'line',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM,
  filter: ['==', ['geometry-type'], 'Polygon'],
  paint: {
    'line-color': 'rgba(247, 251, 45, 0.75)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
  },
};

// Item layer styles
const itemShadowStyle: LayerProps = {
  type: 'circle',
  minzoom: MIN_ZOOM,
  'source-layer': SOURCE_LAYER,
  filter: ['==', ['geometry-type'], 'Point'],
  paint: {
    'circle-radius': 22,
    'circle-color': '#000',
    'circle-blur': 1.4,
  },
};

const itemBackgroundStyle: LayerProps = {
  type: 'circle',
  minzoom: MIN_ZOOM,
  'source-layer': SOURCE_LAYER,
  filter: ['==', ['geometry-type'], 'Point'],
  paint: {
    'circle-radius': 14,
    'circle-color': '#F7FB2D',
  },
  layout: {
    'circle-sort-key': ['coalesce', ['get', 'sort'], 0],
  },
};

const getItemSelectedBackgroundStyle = (selectedId: string): LayerProps => ({
  type: 'circle',
  minzoom: MIN_ZOOM,
  'source-layer': SOURCE_LAYER,
  filter: [
    'all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['to-string', ['id']], selectedId],
  ],
  paint: {
    'circle-radius': 14,
    'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
  },
  layout: {
    'circle-sort-key': ['coalesce', ['get', 'sort'], 0],
  },
});

const itemIconStyle: LayerProps = {
  type: 'symbol',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM,
  filter: ['==', ['geometry-type'], 'Point'],
  layout: {
    'icon-image': PowerOutageIcon.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-offset': PowerOutageIcon.offset,
    'icon-size': 1,
  },
};

export const PowerOutageLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi } = usePoisState();

  const selectedId = getSelectedPowerOutageId(selectedPoi);

  return (
    <VisibilityToggledSource
      id={SOURCE_ID}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      visible={visible}
    >
      {/* Area layers (polygons) */}
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        id={AREA_FILL_LAYER_ID}
        {...addVisible(areaFillStyle, visible)}
      />
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        id={AREA_STROKE_LAYER_ID}
        {...addVisible(areaStrokeStyle, visible)}
      />
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        id={AREA_SELECTED_FILL_LAYER_ID}
        {...addVisible(
          getSelectedPatternStyle({
            idKey: ['to-string', ['id']],
            selectedId,
            otherLayerProps: {
              minzoom: MIN_ZOOM,
              'source-layer': SOURCE_LAYER,
              filter: ['==', ['geometry-type'], 'Polygon'],
            },
          }),
          visible,
        )}
      />
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        id={AREA_SELECTED_STROKE_LAYER_ID}
        {...addVisible(
          getSelectedStrokeStyle({
            idKey: ['to-string', ['id']],
            selectedId,
            otherLayerProps: {
              minzoom: MIN_ZOOM,
              'source-layer': SOURCE_LAYER,
              filter: ['==', ['geometry-type'], 'Polygon'],
            },
          }),
          visible,
        )}
      />

      {/* Item layers (points) */}
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        id={ITEM_SHADOW_LAYER_ID}
        {...addVisible(itemShadowStyle, visible)}
      />
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        id={ITEM_BACKGROUND_LAYER_ID}
        {...addVisible(itemBackgroundStyle, visible)}
      />
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        id={ITEM_SELECTED_BACKGROUND_LAYER_ID}
        {...addVisible(getItemSelectedBackgroundStyle(selectedId), visible)}
      />
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        id={ITEM_ICON_LAYER_ID}
        {...addVisible(itemIconStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};
